import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import en from '@/locales/en.json';
import ko from '@/locales/ko.json';
import { syncLanguage } from '@/lib/i18n';

type LocaleCode = 'en' | 'ko';

interface I18nContextType {
  lang: LocaleCode;
  setLang: (code: LocaleCode) => void;
  /** 원본 `__('key.path')` 와 동일한 번역 함수 */
  t: (key: string) => string;
  /** 특정 로케일의 번역을 가져온다 (드롭다운에서 각 언어명 표시용) */
  tLocale: (key: string, locale: LocaleCode) => string;
}

const locales: Record<LocaleCode, Record<string, unknown>> = { en, ko };

const COOKIE_NAME = 'lang';

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name: string, value: string, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}

/**
 * 객체 표기법(dot notation)으로 중첩 값을 가져온다.
 * 예: resolve(obj, 'home.companyDesc.block1.title')
 */
function resolve(obj: Record<string, unknown>, path: string): string {
  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
  return typeof value === 'string' ? value : path;
}

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LocaleCode>(() => {
    const saved = getCookie(COOKIE_NAME);
    return saved === 'ko' ? 'ko' : 'en';
  });

  const setLang = useCallback((code: LocaleCode) => {
    setCookie(COOKIE_NAME, code);
    setLangState(code);
    syncLanguage(code);
  }, []);

  const t = useCallback(
    (key: string) => resolve(locales[lang], key),
    [lang],
  );

  const tLocale = useCallback(
    (key: string, locale: LocaleCode) => resolve(locales[locale], key),
    [],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, tLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
